/**
 * Utility functions for TopNavigationBarStores component
 * Provides menu scope, navigation and path management functions
 *
 * @module components/TopNavigationBarStores/utils
 */
import { NavigationType } from '@experimental-components/TopNavigationBar/types';
import { MENU_ROUTES } from 'utils/menuMaker/constants';
import { reportsTypes } from 'utils/reports';

/**
 * Checks if a path is a menu-related path
 *
 * @param {string} pathname - The path to check
 * @returns {boolean} True if the path is menu-related
 */
export const isMenuPath = (pathname: string): boolean => {
  return pathname.startsWith(MENU_ROUTES.ROOT);
};

/**
 * Transforms title components into a formatted string
 *
 * @param {string} level - The level/category component
 * @param {Array<string>|string} [location] - The location component(s)
 * @returns {string} The formatted title
 */
const transFormTitle = (level: string, location?: Array<string> | string): string => {
  let title = '';
  if (level) title += level;
  if (level && location) title += ' — ';
  if (Array.isArray(location)) title += location.join(', ');
  if (typeof location === 'string') title += location;
  return title;
};

/**
 * Gets the formatted title based on navigation type and context
 *
 * @param {string} type - The navigation type
 * @param {string} [level] - The level/category
 * @param {Array<string>|string} [location] - The location(s)
 * @returns {string} The formatted title
 */
export const getTitle = (type: string, level?: string, location?: Array<string> | string): string => {
  let currentLevel = '';
  let currentLocation = location;

  if (level && type === NavigationType.ORGANIZATION) currentLevel = level;
  if (type === NavigationType.CEAL) currentLevel = 'Centros de almacenamiento';
  if (level && type === NavigationType.STORE_DRAFT) {
    currentLevel = level;
    currentLocation = '';
  }

  return transFormTitle(currentLevel, currentLocation);
};

/**
 * Gets the formatted title based on organization mode
 * Uses translation keys to avoid hardcoded strings
 *
 * @param {boolean} isOrganizationMode - Whether currently in organization mode
 * @param {Function} t - Translation function
 * @param {string} [organizationName] - The organization name
 * @param {string} [storeName] - The store name
 * @returns {string} The formatted title
 */
export const getScopeBasedTitle = (
  isOrganizationMode: boolean,
  t: (key: string) => string,
  organizationName?: string,
  storeName?: string,
): string => {
  if (isOrganizationMode) {
    return `${t('common:topNavigationBar.organization')} - ${organizationName || ''}`;
  }
  return `${t('common:topNavigationBar.store')}: ${storeName || ''}`;
};

/**
 * Gets the appropriate button label based on navigation type
 *
 * @param {string} type - The navigation type
 * @returns {string} The translation key for the button label
 */
export const getLabelButton = (type: string): string => {
  if (type === NavigationType.ORGANIZATION || type === NavigationType.STORE_DRAFT) {
    return 'common:topNavigationBar.buttonGoStore';
  }
  return 'common:topNavigationBar.buttonGoOrg';
};

/**
 * Gets the appropriate button label based on selected scope
 *
 * @param {MENU_SCOPE | null} selectedScope - The selected menu scope
 * @returns {string} The translation key for the button label
 */
export const getScopeBasedButtonLabel = (isStoreMode: boolean): string => {
  // Show opposite action: if in STORE, show "go to ORGANIZATION" and vice versa
  if (isStoreMode) {
    return 'common:topNavigationBar.buttonGoOrg';
  }
  return 'common:topNavigationBar.buttonGoStore';
};

/**
 * Handles redirection to the appropriate page based on current location
 *
 * @param {Object} location - The current location object
 * @param {string} location.pathname - The current path
 * @param {string} location.search - The current query string
 * @param {Function} replace - The function to use for replacement
 * @returns {void}
 */
export const relocatePage = (
  location: { pathname: string; search: string },
  replace: (values: { pathname: string; search: string } | string) => void,
): void => {
  const { pathname, search } = location;

  switch (true) {
    case pathname.includes(`/reports/${reportsTypes.PRODUCTS_SALES}`):
      replace(`/reports/${reportsTypes.PRODUCTS_SALES}/summary`);
      break;

    case pathname.includes(`/reports/${reportsTypes.CASH_REGISTER}`):
      replace(`/reports/${reportsTypes.CASH_REGISTER}/cash-outs`);
      break;

    case pathname.includes(`/reports/${reportsTypes.PAYMENTS}`):
      replace({ pathname: `/reports/${reportsTypes.PAYMENTS}/summary`, search });
      break;

    case pathname.includes(`/reports/${reportsTypes.CANCELLATIONS}`):
      replace(`/reports/${reportsTypes.CANCELLATIONS}/summary`);
      break;

    case pathname.includes(`/reports/${reportsTypes.DISCOUNTS}`):
      replace(`/reports/${reportsTypes.DISCOUNTS}/summary`);
      break;

    case pathname.includes(`/reports/${reportsTypes.CLIENT_DATA}`):
      replace(`/reports/${reportsTypes.CLIENT_DATA}/summary`);
      break;

    case pathname.includes(`/reports/${reportsTypes.TRANSACTIONS_PAY}`) ||
      pathname.includes('/reports/transfers-pay') ||
      pathname.includes('/reports/movements-pay'):
      replace({ pathname: `/reports/${reportsTypes.TRANSACTIONS_PAY}`, search });
      break;

    case pathname.includes('/workforce/roles'):
      replace('/workforce/roles');
      break;

    case pathname.includes('/workforce/people'):
      replace('/workforce/people');
      break;

    case pathname.includes('/invoicing'):
      replace('/invoicing');
      break;

    case pathname.includes('/settings'):
      replace('/settings');
      break;

    case pathname.includes('/subscription'):
      replace('/subscriptions');
      break;

    case pathname.includes('/online-ordering'):
      replace('/online-ordering');
      break;

    case pathname.includes('/menus'):
      replace({ pathname, search: '' });
      break;

    default:
      break;
  }
};
